<script>
import { formButton, formInput, isValidCNPJ, normalizeDocument } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, provide, onMounted, watch } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { validateClientId } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { checkCompliance } from '@/services/ms'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterPJStep1',
  components: {
    RegisterLayout,
    StepProgressBar,
    formInput,
    formButton
  },

  setup() {
    useMeta({ title: 'Cadastro PJ - Etapa 1' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()

    onMounted(() => {
      validateStepAccess(onboardingStore, 'PJ', [], router)
    })

    const step1Data = onboardingStore.getStepData(1) || {}

    const customerValue = ref(step1Data.customer || '')
    const businessNameValue = ref(step1Data.businessName || '')

    const loading = ref(false)

    const isDocumentValid = ref(false)
    const isResponseCompliance = ref(false)

    const customerNotFound = ref(false)

    const emailExists = ref(false)
    const emailExistsMessage = ref('')

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    const customerSchema = yup.object({
      customer: yup
        .string()
        .required()
        .test((value) => isValidCNPJ(value))
    })

    const { handleSubmit, setFieldError } = useForm({
      validationSchema: yup.object({
        customer: yup
          .string()
          .required('CNPJ é obrigatório')
          .test('cnpj', 'CNPJ inválido', (value) => isValidCNPJ(value)),
        businessName: yup
          .string()
          .required('Nome da empresa é obrigatório')
          .min(6, 'Mínimo de 6 caracteres')
          .max(50, 'Máximo de 50 caracteres')
          .test(
            'full-username',
            'Informe pelo menos duas palavras com 2 letras ou mais cada',
            (value) => {
              if (!value) return false
              const words = value.trim().split(/\s+/)
              return words.length >= 2 && words.filter((word) => word.length >= 2).length >= 2
            }
          )
      })
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true
      emailExists.value = false
      emailExistsMessage.value = ''

      try {
        const cnpjExists = await validateCnpjExists()
        if (cnpjExists) {
          loading.value = false
          return
        }

        onboardingStore.setStepData(1, {
          customer: normalizeDocument(customerValue.value),
          businessName: businessNameValue.value
        })

        router.push('/register/pj/step-2')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    const validateCnpjExists = async () => {
      const cleanCnpj = normalizeDocument(customerValue.value)

      try {
        const clientRes = await validateClientId(cleanCnpj)
        if (clientRes?.data?.payload?.exists === true) {
          setFieldError('customer', 'CNPJ já cadastrado')
          toast('CNPJ já cadastrado.', { type: 'error', autoClose: 3000 })
          return true
        }
      } catch (err) {
        handleGlobalError(err, 'Erro ao verificar CNPJ:')
      }

      return false
    }

    watch(customerValue, (customerSearch) => {
      if (customerSearch.length > 2) doActionSubmit()
      if (customerSearch.length === 0) cleanRegister()
    })

    const cleanRegister = async () => {
      isResponseCompliance.value = false

      customerValue.value = ''
      businessNameValue.value = ''
    }

    const doActionSubmit = () => {
      alreadySubmittedOnce.value = true
      customerNotFound.value = false
      return doCheckCustomer()
    }

    const doCheckCustomer = async () => {
      const isValid = await customerSchema.isValid({ customer: customerValue.value })
      if (!isValid) return
      try {
        loading.value = true
        let responseExtractCompliance = ''
        const cleanCustomer = normalizeDocument(customerValue.value)
        const response = await checkCompliance('company', { cnpj: cleanCustomer })
        responseExtractCompliance = response.data.payload

        isDocumentValid.value = responseExtractCompliance.isValid

        const isProd = import.meta.env.VITE_APP_ENVIRONMENT === 'prod'

        if (!isDocumentValid.value && isProd) {
          toast(`CNPJ inválido!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'error'
          })
          customerNotFound.value = true
          return
        }

        isResponseCompliance.value = true
        businessNameValue.value = responseExtractCompliance.name
      } catch (e) {
        if (e.response?.data?.status === 404) {
          toast(`CNPJ não encontrado!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'error'
          })
          customerNotFound.value = true
          return
        } else if (e.response?.data?.type?.includes('under_18')) {
          toast(`Infelizmente seu cadastro não foi aprovado!`, {
            autoClose: 3000,
            theme: 'auto',
            type: 'warning'
          })
          customerNotFound.value = true
          return
        }
        handleGlobalError(e, 'ERR_COMPLIANCE:')
      } finally {
        loading.value = false
      }
    }

    return {
      customerValue,
      customerNotFound,
      businessNameValue,

      loading,
      onSubmitAction
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar
          class="mt-5 mb-5"
          :steps="['Empresa', 'Responsável', 'Demanda', 'Senha']"
          :current="1"
        />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-input
            class="col-12 form-input mb-3"
            label="CNPJ"
            v-model="customerValue"
            id="customer"
            name="customer"
            type="tel"
            :is-invalid="customerNotFound"
            :isDisabled="loading"
          />
          <form-input
            class="col-12 form-input mb-3"
            label="Razão Social"
            v-model="businessNameValue"
            id="businessName"
            name="businessName"
            type="name"
          />
          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading || customerNotFound"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
